import { Alert, App as AntApp, Button, DatePicker, Descriptions, Form, Select, Space } from "antd";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useApplyProfile, useReportError, type StudentProfile } from "../app/students.ts";
import { StudentExitModal } from "./StudentExitModal.tsx";
import { StudentExitPanel } from "./StudentExitPanel.tsx";

type StudentStatus = components["schemas"]["StudentStatus"];

/** Left is not offered here: it needs a leaving reason and goes through the exit dialog. */
const statuses: StudentStatus[] = ["Active", "Frozen", "Graduated"];
const fields = ["status", "changedOn"] as const;

interface StatusForm {
  status?: StudentStatus;
  changedOn?: Dayjs;
}

export function StudentStatusTab({ student, canManage }: { student: StudentProfile; canManage: boolean }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<StatusForm>();
  const applyProfile = useApplyProfile();
  const reportError = useReportError(form, fields);
  const chosen = Form.useWatch("status", form);
  const [exiting, setExiting] = useState(false);

  const change = useMutation({
    mutationFn: async (values: Required<StatusForm>) => {
      const { data, error, response } = await api.POST("/students/{id}/status", {
        params: { path: { id: student.id } },
        body: { status: values.status, changedOn: values.changedOn.format("YYYY-MM-DD") },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (profile) => {
      await applyProfile(profile);
      form.resetFields();
      void message.success(t("student.status.changed"));
    },
    onError: reportError,
  });

  const previous = student.statusChangedOn ? dayjs(student.statusChangedOn) : null;
  const required = { required: true, message: t("family.validation.required") };
  return (
    <Space orientation="vertical" size="large" style={{ width: "100%", maxWidth: 640 }}>
      <Descriptions column={1} bordered size="small" styles={{ label: { width: 180 } }}>
        <Descriptions.Item label={t("student.status.current")}>{t(`familyDetail.statuses.${student.status}`)}</Descriptions.Item>
        <Descriptions.Item label={t("student.status.changedOn")}>{previous ? previous.format("DD.MM.YYYY") : t("student.status.neverChanged")}</Descriptions.Item>
      </Descriptions>

      {canManage && (
        <Form<StatusForm>
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{ changedOn: dayjs() }}
          onFinish={(values) => {
            if (values.status && values.changedOn) change.mutate({ status: values.status, changedOn: values.changedOn });
          }}
        >
          <Form.Item name="status" label={t("student.status.newStatus")} rules={[required]}>
            <Select options={statuses.filter((s) => s !== student.status).map((s) => ({ value: s, label: t(`familyDetail.statuses.${s}`) }))} />
          </Form.Item>
          <Form.Item name="changedOn" label={t("student.status.effectiveOn")} rules={[required]}>
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" disabledDate={(d) => (previous ? d.isBefore(previous, "day") : false)} />
          </Form.Item>
          {chosen === "Frozen" && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("student.status.billingHint")} />}
          <Space wrap>
            <Button type="primary" htmlType="submit" loading={change.isPending}>
              {t("student.status.submit")}
            </Button>
            {student.status !== "Left" && (
              <Button danger onClick={() => setExiting(true)}>
                {t("exit.open")}
              </Button>
            )}
          </Space>
        </Form>
      )}

      <StudentExitPanel studentId={student.id} canManage={canManage} />
      {canManage && <StudentExitModal student={student} open={exiting} onClose={() => setExiting(false)} />}
    </Space>
  );
}
